import React from "react";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { Music, Sparkles, Phone, Star, ArrowRight } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { eventos, WHATSAPP_LINK } from "../data/mock";

const EventosSection = () => {
  const getWhatsAppUrl = (eventoName) => {
    const message = `Olá! Tenho interesse no pacote ${eventoName}. Gostaria de mais detalhes.`;
    return `${WHATSAPP_LINK}?text=${encodeURIComponent(message)}`;
  };

  return (
    <section id="eventos" className="py-20 lg:py-28 gradient-warm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[hsl(var(--coral))] text-sm font-semibold uppercase tracking-wider mb-4">
            Nightlife & Culture
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Events & Experiences
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Feel the energy of Rio at parties, shows and unique experiences.
            Unforgettable nights in Rio de Janeiro.
          </p>
        </motion.div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {eventos.map((evento, index) => (
            <motion.div
              key={evento.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="group h-full overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-white">
                <div className="flex flex-col lg:flex-row">
                  <div className="relative lg:w-2/5 overflow-hidden">
                    <img
                      src={evento.image}
                      alt={evento.name}
                      className="w-full h-56 lg:h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent lg:bg-gradient-to-r" />
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center gap-1 bg-[hsl(var(--coral))] text-white text-xs font-semibold px-3 py-1 rounded-full">
                        <Sparkles className="w-3 h-3" />
                        {evento.hasDetailPage ? "Featured" : "Experience"}
                      </span>
                    </div>
                  </div>
                  <CardContent className="flex-1 p-6 flex flex-col justify-between">
                    <div>
                      <h3 className="font-display text-xl font-semibold text-gray-900 mb-3">
                        {evento.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {evento.description}
                      </p>
                      <div className="mb-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                          What's included:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {evento.includes.map((item, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center gap-1 bg-[hsl(var(--sand))] text-gray-700 text-xs px-3 py-1 rounded-full"
                            >
                              <Star className="w-3 h-3 text-[hsl(var(--golden))]" />
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                        <Music className="w-4 h-4 text-[hsl(var(--coral))]" />
                        <span>{evento.highlights}</span>
                      </div>
                    </div>
                    
                    {/* Conditional rendering based on hasDetailPage */}
                    {evento.hasDetailPage ? (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link to={`/eventos/${evento.detailSlug}`} className="flex-1">
                          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button className="w-full bg-[hsl(var(--coral))] hover:bg-[hsl(var(--warm-orange))] text-white rounded-full py-3 h-auto font-medium gap-2">
                              View Details
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </motion.div>
                        </Link>
                        <motion.a
                          href={getWhatsAppUrl(evento.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="flex-1"
                        >
                          <Button className="w-full bg-[hsl(var(--whatsapp))] hover:bg-[hsl(142_69%_38%)] text-white rounded-full py-3 h-auto font-medium gap-2">
                            <Phone className="w-4 h-4" />
                            WhatsApp
                          </Button>
                        </motion.a>
                      </div>
                    ) : (
                      <motion.a
                        href={getWhatsAppUrl(evento.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button className="w-full bg-[hsl(var(--whatsapp))] hover:bg-[hsl(142_69%_38%)] text-white rounded-full py-3 h-auto font-medium gap-2">
                          <Phone className="w-4 h-4" />
                          Book Now
                        </Button>
                      </motion.a>
                    )}
                  </CardContent>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EventosSection;
